import { createEvent, createStore, sample } from 'effector';
import type { DeviceLogType } from './device-log-type-meta';
import type { SortDir } from './clients-filter.entity';
import { $resetAuth } from './profile-entity';

export interface IDeviceLog {
	id: number;
	clientId: string;
	buildId: string;
	logType: DeviceLogType;
	message: string;
	stackTrace?: string | null;
	threadName?: string | null;
	occurredAt: string | number;
	createdAt: string | number;
	takeStatus?: 0 | 1 | 2 | 3;
	takenFrom?: string;
	comment?: string;
	isOnline?: boolean;
	hasPhone?: boolean;
	phoneCountryCodes?: import('@crave/config/phone-country').PhoneCountryCode[];
	hasUnclassifiedNonEmptyPhone?: boolean;
	pendingLaunch?: boolean;
}

export const $resetPanelState = createEvent();

export const $setIsFetchingDeviceLogs = createEvent<boolean>();
export const $isFetchingDeviceLogs = createStore<boolean>(false).on($setIsFetchingDeviceLogs, (_, payload) => payload).reset($resetPanelState);

export const $setHasMoreDeviceLogs = createEvent<boolean>();
export const $hasMoreDeviceLogs = createStore<boolean>(false).on($setHasMoreDeviceLogs, (_, payload) => payload).reset($resetPanelState);

export const $setCursorDeviceLogs = createEvent<string | null>();
export const $cursorDeviceLogs = createStore<string | null>(null).on($setCursorDeviceLogs, (_, payload) => payload).reset($resetPanelState);

export const $setDeviceLogs = createEvent<IDeviceLog[]>();
export const $newDeviceLog = createEvent<{ deviceLog: IDeviceLog; sortDir: SortDir }>();
export const $addDeviceLogs = createEvent<IDeviceLog[]>();
export const $updateDeviceLogsByClientId = createEvent<Partial<IDeviceLog> & { clientId: string }>();

export const $deviceLogs = createStore<IDeviceLog[]>([])
	.on($setDeviceLogs, (_, payload) => payload)
	.on($addDeviceLogs, (state, payload) => {
		if (!payload.length) return state;
		const existingIds = new Set(state.map(item => item.id));
		const fresh = payload.filter(item => !existingIds.has(item.id));
		return fresh.length ? [...state, ...fresh] : state;
	})
	.on($newDeviceLog, (state, { deviceLog, sortDir }) => {
		if (state.some(item => item.id === deviceLog.id)) return state;
		return sortDir === 'asc' ? [...state, deviceLog] : [deviceLog, ...state];
	})
	.on($updateDeviceLogsByClientId, (state, payload) =>
		state.map(item => (item.clientId === payload.clientId ? { ...item, ...payload, id: item.id } : item)),
	)
	.reset($resetPanelState);

sample({
	clock: $resetAuth,
	target: $resetPanelState,
});
